import React, { useContext, useEffect, useState } from "react";
import { Avatar, Typography, Divider, Box, Button, IconButton } from "@material-ui/core";
import {
	WhatsApp,
	Instagram,
	Facebook,
	Twitter,
	Language,
	LocationOn,
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { getInitials, handleAddress } from "~/utils/business";
import { useResponsiveText } from "~/hooks/useResponsiveText";
import MuiStylesBusinessProfile from "./MuiStylesBusinessProfile";
import businessService from "~/services/businessService";
import businessAction from "~/store/actions/businessAction";
import { useDispatch, useSelector } from "react-redux";
import SpinnerLoading from "../common/spinnerLoading/SpinnerLoading";
import { DialogBuilder } from "~/utils/DialogProvider";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import BackButton from "~/components/common/backButton";
import { getColorFromUrl, isColorUrl } from "~/utils/business";

const BusinessProfile = (props) => {
	const classes = MuiStylesBusinessProfile();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const textSize = useResponsiveText({ title: 28, subtitle: 16, body: 16 });
	const dialog = useContext(DialogBuilder);

	const [loading, setLoading] = useState(false);

	const { business: businessFromStore } = useSelector((store) => store.business);
	const [business, setBusiness] = useState(businessFromStore || null);

	const backgroundImage = business?.photos?.[0]?.url || business?.categories?.[0]?.image || "";
	const isColorUrlResult = isColorUrl(backgroundImage);
	const colorBackground = isColorUrlResult ? getColorFromUrl(backgroundImage) : null;
	const phone = business?.phoneNumber || business?.phone || "";
	const address = handleAddress(business);

	const portfolioPhotos = business?.photos?.slice(1, 5) || [];

	const handleWhatsAppClick = () => {
		const cleanPhone = phone.replace(/\D/g, "");
		window.open(`https://wa.me/${cleanPhone}`, "_blank");
	};

	const handlePhoneClick = () => {
		window.location.href = `tel:${phone}`;
	};

	const handleEmailClick = () => {
		if (business?.email) {
			window.location.href = `mailto:${business.email}`;
		}
	};

	const handleInstagramClick = () => {
		if (business?.social?.instagram) {
			const baseUrl = "https://instagram.com/";
			window.open(baseUrl + business?.social?.instagram, "_blank");
		}
	};

	const handleTwitterClick = () => {
		if (business?.social?.twitter) {
			const baseUrl = "https://twitter.com/";
			window.open(baseUrl + business?.social?.twitter, "_blank");
		}
	};

	const handleFacebookClick = () => {
		if (business?.social?.facebook) {
			const baseUrl = "https://www.facebook.com/";
			window.open(baseUrl + business?.social?.facebook, "_blank");
		}
	};

	const handleWebsiteClick = () => {
		if (business?.social?.desktop) {
			window.open("https://" + business.social.desktop, "_blank");
		}
	};

	const handleAddressClick = () => {
		if (address) {
			const encodedAddress = encodeURIComponent(address);
			const url = `https://www.google.com/maps/search/${encodedAddress}`;
			window.open(url, "_blank");
		}
	};

	const handleScheduleClick = () => {
		if (business?.id) {
			props.history.push(`/business/${business.id}`);
		}
	};

	useEffect(() => {
		if (business?.id) {
			loadBusiness();
		}
	}, []);

	const loadBusiness = async () => {
		try {
			setLoading(true);
			await businessService
				.getBusiness(business?.id)
				.then((res) => {
					setBusiness(res.data);
					dispatch(businessAction.setBusiness(res.data));
				})
				.finally(() => {
					setLoading(false);
				});
		} catch (error) {
			setLoading(false);
			dialog
				.withMessage(error?.response?.data?.message || t("Unable to load business information"))
				.withButtonText(t("Try again"))
				.withAction(() => {
					loadBusiness();
				})
				.show();
		}
	};

	return (
		<>
			<SpinnerLoading loading={loading} />
			<Box className={classes.container}>
				<BackButton position="absolute" top={16} left={16} />
				{!isColorUrlResult ? (
					<img src={backgroundImage} alt={business?.name} className={classes.backgroundImage} />
				) : (
					<Box
						className={classes.backgroundImage}
						style={{
							backgroundColor: colorBackground || "#f5f5f5",
							height: "180px",
						}}
					/>
				)}

				<Box className={classes.profileCard} style={isColorUrlResult ? { marginTop: "180px" } : {}}>
					<Box className={classes.avatarContainer}>
						<Avatar src={business?.thumbnailUrl} alt={business?.name} className={classes.avatar}>
							{!business?.thumbnailUrl && getInitials(business?.name)}
						</Avatar>
					</Box>

					<Typography variant="h4" className={classes.businessName} style={{ fontSize: textSize.title }}>
						{business?.name}
					</Typography>

					{address && (
						<Box 
							onClick={handleAddressClick}
							className={classes.addressContainer}
						>
							<LocationOn style={{ color: "#666", fontSize: "20px" }} />
							<Typography variant="body1" className={classes.addressText} style={{ fontSize: textSize.subtitle }}>
								{address}
							</Typography>
						</Box>
					)}

					<Box className={classes.socialButtons}>
						{phone && (
							<IconButton
								onClick={handleWhatsAppClick}
								className={`${classes.socialIconButton} ${classes.whatsappButton}`}>
								<WhatsApp className={classes.socialIcon} style={{ color: "#fff", }} />
							</IconButton>
						)}

						{business?.social?.instagram && (
							<IconButton
								onClick={handleInstagramClick}
								className={`${classes.socialIconButton} ${classes.instagramButton}`}>
								<Instagram className={classes.socialIcon} style={{ color: "#fff" }} />
							</IconButton>
						)}

						{business?.social?.twitter && (
							<IconButton
								onClick={handleTwitterClick}
								className={`${classes.socialIconButton} ${classes.twitterButton}`}>
								<Twitter className={classes.socialIcon} style={{ color: "#fff" }} />
							</IconButton>
						)}

						{business?.social?.facebook && (
							<IconButton
								onClick={handleFacebookClick}
								className={`${classes.socialIconButton} ${classes.facebookButton}`}>
								<Facebook className={classes.socialIcon} style={{ color: "#fff" }} />
							</IconButton>
						)}

						{business?.social?.desktop && (
							<IconButton
								onClick={handleWebsiteClick}
								className={`${classes.socialIconButton} ${classes.websiteButton}`}>
								<Language className={classes.socialIcon} style={{ color: "#fff" }} />
							</IconButton>
						)}
					</Box>

					<>
						<Divider className={classes.divider} />
						<Box
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: "16px",
							}}>
							<Typography
								variant="h5"
								className={classes.sectionTitle}
								style={{ fontSize: textSize.subtitle + 8, marginBottom: 0 }}>
								{t("About us")}
							</Typography>
							<Button
								onClick={handleScheduleClick}
								className={classes.scheduleButton}
								style={{
									backgroundColor: "#3f51b5",
									color: "#fff",
									textTransform: "none",
									fontWeight: 600,
									borderRadius: "30px",
									padding: "8px 24px",
								}}>
								{t("Schedule")}
							</Button>
						</Box>

						<Typography 
							variant="body2" 
							className={classes.aboutText} 
							style={{ 
								fontSize: textSize.body + 4,
							}}>
							{business?.description || t("About us empty")}
						</Typography>
					</>

					<>
						<Divider className={classes.divider} style={{ margin: "32px 0" }} />

						<Box className={classes.portfolioSection}>
							<Typography variant="h5" className={classes.sectionTitle} style={{ fontSize: textSize.subtitle + 8 }}>
								{t("Portfolio")}
							</Typography>

							{portfolioPhotos.length > 0 ? (
								<Box className={classes.portfolioGrid}>
									{portfolioPhotos.map((photo, index) => (
										<img
											key={index}
											src={photo?.url}
											alt={`${business?.name} - foto ${index + 1}`}
											className={classes.portfolioImage}
											onClick={() => window.open(photo?.url, "_blank")}
										/>
									))}
								</Box>
							) : (
								<Typography 
									variant="body2" 
									className={classes.aboutText} 
									style={{ 
										fontSize: textSize.body + 4, 
										marginTop: "16px",
									}}>
									{t("Portfolio empty")}
								</Typography>
							)}
						</Box>
					</>

					<Box className={classes.footer}>
						<Typography variant="body2" style={{ color: "#999", fontSize: textSize.body }}>
							{t("Footer")}
						</Typography>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default withRouter(BusinessProfile);
